import React from "react";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import closeIcon from "@src/assets/svg/close-icon-gray.svg";
import styles from "./ota-accept-and-create-backup-process-modal.module.scss";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { OTASecondaryButton } from "@src/components/atoms/buttons/ota-secondary-button/ota-secondary-button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { useOtaAcceptAndCreateBackupProcessModal } from "./use-ota-accept-and-create-backup-process-modal";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";

const cssClassName = "ota-accept-and-create-backup-process-modal";

const OTAAcceptAndCreateBackupProcessModal = ({
  data,
  setInstallAndActionBlock,
}: {
  data: ActiveInstallationProcessPayload;
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >;
}) => {
  const {
    texts,
    machine,
    targetPath,
    isWaitingForFolderStructure,
    target_path_folders,
    handleCloseModal,
    handleAcceptAndCreateBackup,
    isAcceptAndCreateBackupLoading,
  } = useOtaAcceptAndCreateBackupProcessModal(data, setInstallAndActionBlock);

  const targetPathPlaceholder = "{target_path}";
  const [
    backupPathPrefix = texts.backupPathContainsSectionTitle,
    backupPathSuffix = "",
  ] = texts.backupPathContainsSectionTitle.split(targetPathPlaceholder);
  const hasTargetPathPlaceholder =
    texts.backupPathContainsSectionTitle.includes(targetPathPlaceholder);

  return (
    <div className={styles[cssClassName]}>
      <div
        className={styles[`${cssClassName}__overlay`]}
        aria-label={texts.closeModalAriaLabel}
      />
      <div className={styles[`${cssClassName}__dialog-wrapper`]}>
        <div
          className={styles[`${cssClassName}__modal-body`]}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles[`${cssClassName}__header`]}>
            <h2 className={styles[`${cssClassName}__title`]}>{texts.title}</h2>
            <IconButton
              fun={handleCloseModal}
              icon={closeIcon}
              tooltipPosition="top"
              variant="close-button"
              disabled={isAcceptAndCreateBackupLoading}
            />
          </div>

          <div className={styles[`${cssClassName}__form`]}>
            <FlexColumnContainer verticalGap={20} alignItems="stretch">
              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.targetPathLabel}
                </p>
                <div className={styles[`${cssClassName}__info-card`]}>
                  <p className={styles[`${cssClassName}__path-text`]}>
                    {targetPath}
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.sourceMachineLabel}
                </p>
                <div className={styles[`${cssClassName}__machine-card`]}>
                  <p className={styles[`${cssClassName}__machine-name`]}>
                    {machine.name}
                    {machine.ip && (
                      <span className={styles[`${cssClassName}__machine-ip`]}>
                        ({machine.ip})
                      </span>
                    )}
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {hasTargetPathPlaceholder ? (
                    <>
                      {backupPathPrefix}
                      <span
                        className={styles[`${cssClassName}__target-path-bold`]}
                      >
                        {targetPath}
                      </span>
                      {backupPathSuffix}
                    </>
                  ) : (
                    texts.backupPathContainsSectionTitle
                  )}
                </p>
                <div
                  className={`${styles[`${cssClassName}__folders-list-card`]} ${
                    styles[`${cssClassName}__target-folders-card`]
                  }`}
                >
                  {target_path_folders?.map((entry) => (
                    <p
                      key={`target-folder-${entry}`}
                      className={styles[`${cssClassName}__folder-name`]}
                    >
                      \{entry}
                    </p>
                  ))}
                </div>
              </FlexColumnContainer>

              <p className={styles[`${cssClassName}__warning-text`]}>
                {texts.warningText}
              </p>
            </FlexColumnContainer>
          </div>

          <div className={styles[`${cssClassName}__buttons-wrapper`]}>
            <OTASecondaryButton
              fun={handleCloseModal}
              text={texts.cancelButtonText}
              disabled={isAcceptAndCreateBackupLoading}
            />
            <ButtonWithLoader
              fun={handleAcceptAndCreateBackup}
              text={texts.acceptAndCreateBackupButtonText}
              loading={isAcceptAndCreateBackupLoading}
              disabled={isWaitingForFolderStructure}
              tooltip={isWaitingForFolderStructure ? texts.waitMessage : ""}
              tooltipPosition="top"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTAAcceptAndCreateBackupProcessModal;
